import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { OrderDto } from '../dto/order';
import {
  buildManualWZCreatePayload,
  createManualWZ,
  fetchWZCreateOrder,
  fetchWZCreateOrderOptions,
  fetchWZCreatePrefill,
  generateFullyCoveredWZ,
  mapWZPrefillItem,
  type WZCreateHandoverValues,
  type WZCreateItemDraft,
} from '../modules/wz/api/wzCreateApi';
import { getApiErrorMessage } from '../utils/apiError';
import { formatProductDimensions } from '../utils/productDimensions';
import { WZ_QUANTITY_MAX } from '../utils/warehouseQuantity';
import { WZ_SERVER_ERROR_KEYS } from '../utils/wzServerErrorKeys';
import {
  isWZOrderFullyCoveredError,
  showWZFullyCoveredConfirm,
} from '../utils/wzOrderFullyCoveredMessage';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import {
  convertWZItemsToTarget,
  type WZBatchConversionTarget,
} from '../modules/wz/wzBatchQuantityConversion';
import WZBatchConversionMenu from '../modules/wz/components/WZBatchConversionMenu';
import './WZItemsView.css';
import './WZNew.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface WZCreateFormValues extends Omit<WZCreateHandoverValues, 'issueDate' | 'receivedDate'> {
  orderId: string;
  issueDate: Dayjs;
  receivedDate: Dayjs;
}

const formatWZItemDimensions = (item: WZCreateItemDraft) => formatProductDimensions({
  width: item.width,
  height: item.height,
  length: item.length,
  thickness: item.thickness,
  productCategoryDimensionDisplayProfile: 'GENERIC',
}) || '—';

const WZNew: React.FC = () => {
  const { t } = useTranslation(['wz', 'common', 'orders', 'transport']);
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialOrderId = searchParams.get('orderId');
  const [form] = Form.useForm();

  const [orderOptions, setOrderOptions] = useState<OrderDto[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDto | null>(null);
  const [items, setItems] = useState<WZCreateItemDraft[]>([]);
  const [isFullyCoveredOrder, setIsFullyCoveredOrder] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);
  const orderRequestRef = useRef(0);
  const prefillRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadOrderOptions = async () => {
      setOrdersLoading(true);
      try {
        const options = await fetchWZCreateOrderOptions();
        if (cancelled) return;
        setOrderOptions(options);
      } catch (error) {
        console.error('Error loading orders for WZ:', error);
        if (!cancelled) message.error(t('wz:errors.loadOrder'));
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };

    void loadOrderOptions();
    return () => { cancelled = true; };
  }, [message, t]);

  const applyOrder = useCallback(async (order: OrderDto) => {
    const prefillRequestId = ++prefillRequestRef.current;
    setSelectedOrder(order);
    setItems([]);
    setIsFullyCoveredOrder(false);
    setItemsLoading(true);
    const registeredAddress = order.customer?.address || '';
    const deliveryAddress = order.deliveryLocation || registeredAddress;
    setDeliveryDifferent(Boolean(deliveryAddress && deliveryAddress !== registeredAddress));
    form.setFieldValue('deliveryAddress', deliveryAddress);

    try {
      const prefill = await fetchWZCreatePrefill(order.id);
      if (prefillRequestRef.current !== prefillRequestId) return;

      const nextItems = prefill.items.map(mapWZPrefillItem);
      setItems(nextItems);
      const fullyCovered = nextItems.length === 0 && prefill.orderHasProducts;
      setIsFullyCoveredOrder(fullyCovered);
      if (fullyCovered) {
        message.info(t('wz:pickOrder.fullyCoveredHint'));
      }
    } catch (error) {
      if (prefillRequestRef.current !== prefillRequestId) return;
      console.error('Error loading WZ items from order:', error);
      setItems([]);
      setIsFullyCoveredOrder(false);
      message.error(t('wz:errors.loadOrder'));
    } finally {
      if (prefillRequestRef.current === prefillRequestId) setItemsLoading(false);
    }
  }, [form, message, t]);

  const loadOrder = useCallback(async (orderId: string) => {
    const orderRequestId = ++orderRequestRef.current;
    ++prefillRequestRef.current;
    setSelectedOrder(null);
    setItems([]);
    setIsFullyCoveredOrder(false);
    setItemsLoading(false);
    setOrderLoading(true);

    try {
      const order = await fetchWZCreateOrder(orderId);
      if (orderRequestRef.current !== orderRequestId) return;
      form.setFieldValue('orderId', order.id);
      await applyOrder(order);
    } catch (error) {
      if (orderRequestRef.current !== orderRequestId) return;
      console.error('Error loading order for WZ:', error);
      form.setFieldValue('orderId', undefined);
      message.error(t('wz:errors.loadOrder'));
    } finally {
      if (orderRequestRef.current === orderRequestId) setOrderLoading(false);
    }
  }, [applyOrder, form, message, t]);

  useEffect(() => {
    if (initialOrderId) void loadOrder(initialOrderId);
  }, [initialOrderId, loadOrder]);

  const selectedOrderLabel = selectedOrder
    ? [
      selectedOrder.displayOrderNumber || selectedOrder.orderNumber,
      selectedOrder.customerName || selectedOrder.customer?.name,
    ].filter(Boolean).join(' — ')
    : '';

  const handleQuantityChange = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index
        ? {
          ...item,
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          saleQuantity: patch.pricingQuantity ?? null,
          saleUnit: patch.pricingUnit ?? null,
          ...(patch.unit ? { unit: patch.unit } : {}),
        }
        : item
    )));
  };

  const handleBatchConversion = (target: WZBatchConversionTarget) => {
    const result = convertWZItemsToTarget(items, target);
    setItems(result.items);

    if (result.convertedCount === 0) {
      message.warning(t('wz:createForm.batchConversionNone'));
      return;
    }
    if (result.skippedCount > 0) {
      message.warning(t('wz:createForm.batchConversionPartial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
      return;
    }
    message.success(t('wz:createForm.batchConversionSuccess', { count: result.convertedCount }));
  };

  const handleSubmit = async (values: WZCreateFormValues) => {
    if (!selectedOrder || selectedOrder.id !== values.orderId) {
      message.error(t('wz:validation.orderRequired'));
      return;
    }

    const validItems = items.filter((item) => item.productName.trim() !== '');
    if (validItems.length === 0 && !isFullyCoveredOrder) {
      message.error(t('wz:errors.noItems'));
      return;
    }

    const issueDate = values.issueDate.format('YYYY-MM-DD');
    const receivedDate = values.receivedDate.format('YYYY-MM-DD');
    const handover: WZCreateHandoverValues = {
      ...values,
      issueDate,
      receivedDate,
      deliveryAddress: deliveryDifferent
        ? values.deliveryAddress
        : selectedOrder.customer?.address || '',
    };
    setSubmitting(true);
    try {
      let result;
      if (isFullyCoveredOrder) {
        try {
          result = await generateFullyCoveredWZ(selectedOrder.id, handover);
        } catch (error) {
          if (!isWZOrderFullyCoveredError(error)) throw error;
          const confirmed = await showWZFullyCoveredConfirm({ error, modal, t });
          if (!confirmed) return;
          result = await generateFullyCoveredWZ(selectedOrder.id, handover, true);
        }
      } else {
        const payload = buildManualWZCreatePayload({
          order: selectedOrder,
          items: validItems,
          handover,
          issueDate,
          releaseDate: issueDate,
          receivedDate,
        });
        result = await createManualWZ(payload);
      }
      message.success(t('wz:messages.createSuccess'));
      const createdId = result?.id;
      navigate(createdId ? `/app/wz/${createdId}` : '/app/wz');
    } catch (error) {
      console.error('Error creating WZ document:', error);
      message.error(getApiErrorMessage(error, t, 'wz:errors.createFailed', WZ_SERVER_ERROR_KEYS));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wz-create-page">
      <div className="wz-create-page__header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/app/wz')}
          aria-label={t('common:back')}
        />
        <div>
          <Title level={2} className="wz-create-page__title">{t('wz:newDocument')}</Title>
          <Text type="secondary">{t('wz:createForm.description')}</Text>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={() => message.warning(t('wz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          issuedBy: '',
          receivedBy: t('transport:driver'),
          issueDate: dayjs(),
          receivedDate: dayjs(),
        }}
      >
        <Space direction="vertical" size={20} className="wz-create-page__content">
          <Card title={t('wz:createForm.orderSection')}>
            <Form.Item
              name="orderId"
              label={t('wz:fields.order')}
              rules={[{ required: true, message: t('wz:validation.orderRequired') }]}
              className="wz-create-page__order-field"
            >
              <Select
                showSearch
                loading={ordersLoading || orderLoading}
                disabled={orderLoading}
                placeholder={t('wz:placeholders.selectOrder')}
                optionFilterProp="label"
                onChange={(value) => void loadOrder(value)}
                options={orderOptions.map((order) => ({
                  value: order.id,
                  label: `${order.displayOrderNumber || order.orderNumber} — ${order.customerName || order.customer?.name || '—'}`,
                }))}
              />
            </Form.Item>

            {selectedOrder && (
              <>
                <Descriptions
                  size="small"
                  column={1}
                  className="wz-create-page__order-summary"
                >
                  <Descriptions.Item label={t('wz:fields.orderNumber')}>
                    {selectedOrder.displayOrderNumber || selectedOrder.orderNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('wz:fields.recipient')}>
                    {selectedOrder.customerName || selectedOrder.customer?.name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('wz:fields.recipientAddress')}>
                    {selectedOrder.customer?.address || '—'}
                  </Descriptions.Item>
                </Descriptions>
                <div className="wz-create-page__delivery-address">
                  <Checkbox
                    checked={deliveryDifferent}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setDeliveryDifferent(checked);
                      form.setFieldValue(
                        'deliveryAddress',
                        checked
                          ? selectedOrder.deliveryLocation || ''
                          : selectedOrder.customer?.address || '',
                      );
                    }}
                  >
                    {t('wz:delivery.differentFromRegistered')}
                  </Checkbox>
                  {deliveryDifferent && (
                    <Form.Item
                      name="deliveryAddress"
                      label={t('wz:fields.deliveryAddress')}
                      rules={[{ required: true, message: t('wz:validation.deliveryAddressRequired') }]}
                    >
                      <TextArea rows={2} placeholder={t('wz:placeholders.deliveryAddress')} />
                    </Form.Item>
                  )}
                </div>
              </>
            )}
          </Card>

          <Card title={t('wz:createForm.datesSection')}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="issueDate"
                  label={t('wz:fields.issueDate')}
                  rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="receivedDate"
                  label={t('wz:fields.deliveryDate')}
                  rules={[{ required: true, message: t('wz:validation.deliveryDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('wz:createForm.itemsSection')}
            className="wz-items-section"
            extra={items.length > 1 ? (
              <WZBatchConversionMenu onConvert={handleBatchConversion} />
            ) : null}
          >
            <Spin spinning={itemsLoading}>
              {!selectedOrder || items.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={selectedOrder
                    ? t(isFullyCoveredOrder ? 'wz:createForm.fullyCoveredAction' : 'wz:createForm.noItems')
                    : t('wz:createForm.selectOrderFirst')}
                />
              ) : (
                <WZItemsList>
                  {items.map((item, index) => (
                    <WZItemCard
                      key={`${item.productVariantId || item.productName}-${index}`}
                      index={index}
                      positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                      productLabel={t('wz:fields.productName')}
                      product={item.productName}
                    >
                      <WZItemField label={t('wz:fields.dimensions')} wide>
                        {formatWZItemDimensions(item)}
                      </WZItemField>
                      <WZItemField label={t('wz:fields.quantity')} emphasis>
                        <WarehouseQuantityField
                          line={item}
                          pricingQuantity={item.saleQuantity}
                          pricingUnit={item.saleUnit}
                          onChange={(patch) => handleQuantityChange(index, patch)}
                          maxQuantity={WZ_QUANTITY_MAX}
                          maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                          noDimensionsLabel={t('wz:conversion.noDimensions')}
                          unitLabel={t('wz:fields.unit')}
                        />
                      </WZItemField>
                    </WZItemCard>
                  ))}
                </WZItemsList>
              )}
            </Spin>
          </Card>

          <Card title={t('wz:createForm.releaseSection')}>
            <Alert
              type="info"
              showIcon
              message={t('wz:createForm.releaseHint')}
              className="wz-create-page__release-hint"
            />
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="issuedBy"
                  label={t('wz:fields.issuedBy')}
                  rules={[
                    { required: true, message: t('wz:validation.issuedByRequired') },
                    { max: 100, message: t('wz:validation.personNameMax') },
                  ]}
                >
                  <Input placeholder={t('wz:placeholders.issuedBy')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="vehicleRegistration"
                  label={t('wz:fields.vehicleRegistration')}
                >
                  <Input placeholder={t('wz:placeholders.vehicleRegistration')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="receivedBy"
                  label={t('wz:fields.receivedBy')}
                  rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                >
                  <Input placeholder={t('wz:placeholders.receivedBy')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="notes" label={t('wz:fields.notes')}>
                  <TextArea rows={4} placeholder={t('wz:placeholders.notes')} maxLength={1000} showCount />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div className="wz-create-page__actions">
            <Button onClick={() => navigate('/app/wz')}>{t('common:cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!selectedOrder || itemsLoading || orderLoading || (items.length === 0 && !isFullyCoveredOrder)}
            >
              {t('wz:actions.create')}
            </Button>
          </div>

          {selectedOrderLabel && (
            <Text type="secondary" className="wz-create-page__selection-note">
              {t('wz:createForm.selectedOrder', { order: selectedOrderLabel })}
            </Text>
          )}
        </Space>
      </Form>
    </div>
  );
};

export default WZNew;
